import clsx from "clsx";
import React from "react";
import {SupportedLanguages} from "@/enums/internationalization";
import {useUserProfile} from "@/hooks/useUserProfile";
import {BannerItem, BannerLink} from "@/types/common/banner";
import Button from "./Button";
import Icon from "./Icon";

interface BannerProps {
   item: BannerItem;
   isPublic?: boolean;
   isPreview?: boolean;
   handleRemoveBanner: (id: string) => Promise<void>;
}

function getResolvedLinks(item: BannerItem): BannerLink[] {
   if (item.links && item.links.length > 0) return item.links;

   if ((item.link || item.linkId) && item.linkText) {
      return [
         {
            type: item.linkId ? "id" : "url",
            value: item.linkId || item.link || "",
            text: item.linkText,
         },
      ];
   }

   return [];
}

const Banner: React.FC<BannerProps> = ({item, isPublic, isPreview, handleRemoveBanner}) => {
   const {userProfile} = useUserProfile();
   const isPrimary = item.variant === "primary";
   const hasDarkBg = isPrimary || (!isPrimary && item.status === "error");
   const hasLightColoredBg = !isPrimary && (item.status === "warning" || item.status === "success");
   const resolvedLinks = getResolvedLinks(item);

   const getLinkText = (link: BannerLink) =>
      Object.keys(link.text).includes(userProfile.language)
         ? link.text[userProfile.language]
         : link.text[SupportedLanguages.English];

   return (
      <div
         className={clsx("flex", "w-full", "justify-between", "items-center", "p-4", "z-30", {
            relative: !isPublic,
            absolute: isPublic,
            "rounded-lg": isPreview,
            "bg-primary": isPrimary,
            "bg-surface": !isPrimary && item.status === "info",
            "bg-orange-300 dark:bg-yellow-900": !isPrimary && item.status === "warning",
            "bg-red-400 dark:bg-red-800": !isPrimary && item.status === "error",
            "bg-[#81c781] dark:bg-green-900": !isPrimary && item.status === "success",
         })}
      >
         <div
            className={clsx(
               "flex flex-wrap md:flex-row md:items-center md:justify-center gap-3",
               "text-sm font-normal flex-1",
               {"text-800": !hasDarkBg, "text-white": hasDarkBg}
            )}
         >
            <div className="flex items-center">
               {item.icon && <Icon icon={item.icon} size={2} className="mr-3 shrink-0" />}
               <span className="[&_p]:inline">
                  {Object.keys(item.description).includes(userProfile.language)
                     ? item.description[userProfile.language]
                     : item.description[SupportedLanguages.English]}
               </span>
            </div>
            {resolvedLinks.length > 0 && (
               <div className="flex flex-row items-center gap-2 flex-wrap">
                  {resolvedLinks.map((link, index) => (
                     <Button
                        key={`${link.value}-${index}`}
                        id={link.type === "id" ? link.value : `bannerLink-${index}`}
                        href={link.type === "url" && link.value.length > 0 ? link.value : undefined}
                        type="tertiary"
                        compact
                        className={clsx("whitespace-nowrap", {
                           "!border-white !text-white hover:!bg-white/15": hasDarkBg,
                           "!border-black/30 !text-black/80 hover:!bg-black/10": hasLightColoredBg,
                        })}
                        target={
                           link.type === "url" && link.value.startsWith("http") ? "_blank" : "_self"
                        }
                     >
                        {getLinkText(link)}
                     </Button>
                  ))}
               </div>
            )}
         </div>
         {!item.isPersistent && (
            <Button
               iconOnly
               type="none"
               className={clsx("shrink-0", {
                  "text-800": !hasDarkBg,
                  "text-white": hasDarkBg,
               })}
               onClick={() => handleRemoveBanner(item.id)}
            >
               <Icon icon="xmark" />
            </Button>
         )}
      </div>
   );
};

export default Banner;
